import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/*
 * Source hover for GenBI citation chips.
 *
 * GenBI responses arrive as HTML strings, so the chips can't be wrapped in a
 * React component the way ModelDataViewer wraps cells in SourceTooltip. The
 * backend puts data-ref="[doc_id_N]" (or "[ar_id_N]") on every citation chip,
 * and data-src="src_..." on each doc chip it could link to a source passage.
 * This hook listens for those chips (event delegation).
 *
 * Every document chip gets a card:
 *   - doc chip with a source   -> document, quoted phrase, View (opens and highlights)
 *   - doc chip without a source -> document, "no exact passage linked", View (no highlight)
 * Reference-library chips get none: there is no file behind them.
 *
 * Usage, in the component that renders agent responses:
 *
 *   const lookup = useMemo(() => buildCitationSourceLookupFromResponses(agent.response), [...]);
 *   const { containerRef, scopeProps, hover, keepOpen, scheduleHide, close } =
 *     useCitationSourceHover(lookup, { enabled: !isEditing });
 *
 *   <div ref={containerRef}>...rendered responses...</div>
 *   // anything this component renders through a portal that holds chips:
 *   createPortal(<div {...scopeProps}>...</div>, document.body)
 *
 *   <CitationSourceCard
 *     hover={hover} lookup={lookup} docNames={{ 205: 'file.pdf' }}
 *     onMouseEnter={keepOpen} onMouseLeave={scheduleHide}
 *     onView={(p) => { close(); openSourcePreview(p); }}
 *   />
 *
 *   // openSourcePreview stores p and renders (phrase/chunk/caption are null for
 *   // a chip without a source, so the document opens with nothing highlighted):
 *   <DocumentPreview isOpen docId={p.docId} documentName={p.documentName}
 *     highlightText={p.phrase} highlightChunkContent={p.chunkContent}
 *     highlightTableCaption={p.tableCaption} onClose={...} />
 *
 * containerRef is a callback ref, not a ref object: the container unmounts and
 * remounts (edit mode, status changes), and the listeners must move with it.
 *
 * Listeners sit on the document. A chip is handled only when its nearest
 * [data-citation-scope] ancestor is this instance's container or one of its
 * portals, so two mounted responses never both react, and chips that are not
 * inside any scope (editors, other screens) are left alone.
 */

const CARD_WIDTH = 280;
// Above the figure lightbox (whose z-index is clamped to the maximum), so the
// card shows for chips in a lightbox caption too. Later in the DOM wins a tie.
const CARD_Z_INDEX = 2147483647;
const VIEWPORT_MARGIN = 12;
const HIDE_DELAY_MS = 200;
const SCOPE_ATTR = 'data-citation-scope';
const CHIP_SELECTOR = '[data-src], [data-ref*="doc_id_"], [data-ref*="ar_id_"]';
const TOKEN_RE = /\b(doc|ar)_id_(\d+)/;
const NAME_ATTR = 'data-citation-name';

let scopeCounter = 0;

const truncate = (text, max) => {
  const t = (text || '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.substring(0, max)}...` : t;
};

/** Merge citation_sources from one or more agent responses. Entry ids are globally unique. */
export const buildCitationSourceLookupFromResponses = (...responses) => {
  const entries = {};
  const chunks = {};
  responses.forEach((r) => {
    const cs = r?.citation_sources;
    if (!cs) return;
    Object.assign(entries, cs.entries || {});
    Object.assign(chunks, cs.chunks || {});
  });
  return { entries, chunks };
};

/** doc_id -> file name from one or more responses' references.doc_ids. */
export const buildDocNameMapFromResponses = (...responses) => {
  const m = {};
  responses.forEach((r) => {
    (r?.references?.doc_ids || []).forEach((d) => {
      if (d?.doc_id != null && d.doc_name) m[d.doc_id] = d.doc_name;
    });
  });
  return m;
};

/** What a chip cites: { kind: 'doc'|'ref', refId, srcId, label } or null. */
const readChip = (chip) => {
  const m = TOKEN_RE.exec(chip.getAttribute('data-ref') || '') || TOKEN_RE.exec(chip.textContent || '');
  if (!m) return null;
  return {
    kind: m[1] === 'ar' ? 'ref' : 'doc',
    refId: parseInt(m[2], 10),
    srcId: chip.getAttribute('data-src') || null,
    // the chip text is shortened; the title, when there is one, has the full name
    label: chip.getAttribute(NAME_ATTR) || chip.getAttribute('title') || (chip.textContent || '').trim(),
  };
};

/** The card replaces the browser's own tooltip: keep the full name, drop the title. */
const muteNativeTooltip = (chip) => {
  const title = chip.getAttribute('title');
  if (!title) return;
  chip.setAttribute(NAME_ATTR, title);
  chip.removeAttribute('title');
};

export const useCitationSourceHover = (lookup, { enabled = true } = {}) => {
  const [root, setRoot] = useState(null);
  const [hover, setHover] = useState(null); // { kind, refId, srcId, label, el, rect }
  const hideTimer = useRef(null);
  const scopeId = useMemo(() => `citation-scope-${++scopeCounter}`, []);
  const scopeProps = useMemo(() => ({ [SCOPE_ATTR]: scopeId }), [scopeId]);

  const containerRef = useCallback((node) => {
    if (node) node.setAttribute(SCOPE_ATTR, scopeId);
    setRoot(node);
  }, [scopeId]);

  const keepOpen = useCallback(() => clearTimeout(hideTimer.current), []);
  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setHover(null), HIDE_DELAY_MS);
  }, []);
  const close = useCallback(() => {
    clearTimeout(hideTimer.current);
    setHover(null);
  }, []);

  useEffect(() => {
    if (!root || !enabled) {
      setHover(null);
      return undefined;
    }

    const chipFrom = (node) => {
      const chip = node && node.closest ? node.closest(CHIP_SELECTOR) : null;
      if (!chip) return null;
      const scope = chip.closest(`[${SCOPE_ATTR}]`);
      return scope && scope.getAttribute(SCOPE_ATTR) === scopeId ? chip : null;
    };

    // While the page scrolls, chips slide under a pointer that is not moving and the
    // browser fires mouseover for each of them, which flashed the card open and shut.
    // Scrolling closes the card and marks the pointer "stale"; the card opens again
    // only after the mouse itself moves.
    let pointerStale = false;
    let lastX = null;
    let lastY = null;

    const openFor = (target) => {
      const chip = chipFrom(target);
      if (!chip) return;
      const info = readChip(chip);
      // A reference-library chip has no file behind it, so there is nothing to show.
      if (!info || info.kind === 'ref') return;
      muteNativeTooltip(chip);
      clearTimeout(hideTimer.current);
      setHover((h) => (h && h.el === chip ? h : { ...info, el: chip, rect: chip.getBoundingClientRect() }));
    };

    const onOver = (e) => {
      if (pointerStale) return;
      openFor(e.target);
    };

    const onOut = (e) => {
      const chip = chipFrom(e.target);
      if (!chip || chip.contains(e.relatedTarget)) return;
      scheduleHide();
    };

    const onMove = (e) => {
      // Browsers also send a mousemove after a scroll, at the same coordinates;
      // only a real change of position counts as the user moving the mouse.
      const moved = e.clientX !== lastX || e.clientY !== lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      if (!pointerStale || !moved) return;
      pointerStale = false;
      openFor(e.target); // mouseover does not fire again for a chip already under the pointer
    };

    const onScroll = (e) => {
      if (e?.target?.closest?.('.source-tooltip-card')) return;
      pointerStale = true;
      clearTimeout(hideTimer.current);
      setHover(null);
    };

    const onResize = () => setHover(null);

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      clearTimeout(hideTimer.current);
    };
  }, [root, enabled, scopeId, scheduleHide]);

  return { containerRef, scopeProps, hover, keepOpen, scheduleHide, close };
};

export const CitationSourceCard = ({ hover, lookup, docNames = {}, onView, onMouseEnter, onMouseLeave }) => {
  const cardRef = useRef(null);
  const [style, setStyle] = useState({ position: 'fixed', visibility: 'hidden', zIndex: CARD_Z_INDEX });

  useLayoutEffect(() => {
    if (!hover || !cardRef.current) return;
    const rect = hover.rect;
    const cardHeight = cardRef.current.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const below = vh - rect.bottom;

    let top = below >= cardHeight + VIEWPORT_MARGIN || below >= rect.top
      ? rect.bottom + 6
      : rect.top - cardHeight - 6;
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - cardHeight - VIEWPORT_MARGIN));
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, vw - CARD_WIDTH - VIEWPORT_MARGIN));

    // width comes from .source-tooltip-card, exactly as in Textractor's SourceTooltip
    setStyle({ position: 'fixed', top, left, zIndex: CARD_Z_INDEX });
  }, [hover]);

  if (!hover) return null;

  const entry = hover.srcId ? lookup?.entries?.[hover.srcId] : null;
  const sources = (entry?.sources || [])
    .map((key) => ({ key, chunk: lookup?.chunks?.[key] }))
    .filter((s) => s.chunk);

  const card = (children) => ReactDOM.createPortal(
    <div
      ref={cardRef}
      className="source-tooltip-card"
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="dialog"
      aria-label="Citation source"
    >
      {children}
    </div>,
    document.body
  );

  // Reference-library citation: there is no file behind it, only its name.
  if (hover.kind === 'ref') {
    return card(
      <div className="source-tooltip-item">
        <div className="source-tooltip-doc-name">
          <i className="bi bi-journal-text me-1"></i>
          {truncate(hover.label, 80) || `Reference ${hover.refId}`}
        </div>
        <div className="source-tooltip-phrase" style={{ fontStyle: 'italic' }}>
          Reference library entry. There is no source document to open.
        </div>
      </div>
    );
  }

  // Document citation with no linked passage: the document opens, nothing is highlighted.
  if (!entry || sources.length === 0) {
    const name = docNames[hover.refId] || hover.label || `Document ${hover.refId}`;
    return card(
      <div className="source-tooltip-item">
        <div className="source-tooltip-doc-name">
          <i className="bi bi-file-earmark-text me-1"></i>
          {truncate(name, 50)}
        </div>
        <div className="source-tooltip-phrase" style={{ fontStyle: 'italic' }}>
          No exact passage is linked to this citation
        </div>
        <button
          type="button"
          className="source-tooltip-view-btn"
          onClick={() =>
            onView &&
            onView({
              docId: hover.refId,
              documentName: name,
              phrase: null,
              chunkContent: null,
              tableCaption: null,
            })
          }
        >
          View <i className="bi bi-eye ms-1"></i>
        </button>
      </div>
    );
  }

  const phrase = entry.phrase_found === false ? '' : truncate(entry.verbatim_phrase, 100);

  return card(
    sources.map(({ key, chunk }, index) => (
      <div
        key={key}
        className={`source-tooltip-item${index < sources.length - 1 ? ' source-tooltip-item-border' : ''}`}
      >
        <div className="source-tooltip-doc-name">
          <i className="bi bi-file-earmark-text me-1"></i>
          {truncate(chunk.doc_title, 50) || `Document ${chunk.doc_id}`}
        </div>
        {phrase && index === 0 && (
          <div className="source-tooltip-phrase">"{phrase}"</div>
        )}
        {entry.approximate && index === 0 && (
          <div className="source-tooltip-phrase" style={{ fontStyle: 'italic' }}>
            The text near this citation was edited, so this source may not match it exactly.
          </div>
        )}
        <button
          type="button"
          className="source-tooltip-view-btn"
          onClick={() =>
            onView &&
            onView({
              docId: chunk.doc_id,
              documentName: chunk.doc_title || `Document ${chunk.doc_id}`,
              // The phrase belongs to the first source (the backend orders the
              // chunk that contains it first); other sources highlight by chunk.
              phrase: index === 0 ? entry.verbatim_phrase || null : null,
              chunkContent: chunk.content || null,
              tableCaption: index === 0 ? entry.table_caption || null : null,
            })
          }
        >
          View <i className="bi bi-eye ms-1"></i>
        </button>
      </div>
    ))
  );
};